<template>
  <div class="products-container">
    <!-- 顶部横幅 -->
    <div class="hero-section" ref="heroSection">
      <div class="hero-content" :style="{ transform: `translateY(${scrollY * 0.5}px)` }">
        <h1 class="animate__animated animate__fadeInUp">产品中心</h1>
        <p class="animate__animated animate__fadeInUp animate__delay-1s">
          创新科技，智造未来
        </p>
      </div>
      <div class="hero-overlay"></div>
    </div>

    <!-- 产品分类导航 -->
    <div class="category-nav">
      <div class="nav-container">
        <div 
          class="nav-item"
          :class="{ active: currentCategory === 'all' }" 
          @click="selectCategory('all')"
        >
          <span class="nav-text">全部产品</span>
          <span class="nav-line"></span>
        </div>
        <div 
          v-for="category in categories" 
          :key="category.id"
          class="nav-item"
          :class="{ active: currentCategory === category.id }"
          @click="selectCategory(category.id)"
        >
          <span class="nav-text">{{ category.name }}</span>
          <span class="nav-line"></span>
        </div>
      </div>
    </div>

    <!-- 产品展示区 -->
    <div class="products-section">
      <div class="products-grid">
        <div 
          v-for="product in filteredProducts" 
          :key="product.id"
          class="product-card"
          v-scrollanimation
          @click="navigateToDetail(product)"
        >
          <div class="card-content">
            <div class="product-image">
              <el-image :src="product.image" fit="contain">
                <template #placeholder>
                  <div class="image-placeholder">
                    <el-icon><Picture /></el-icon>
                  </div>
                </template>
              </el-image>
              <div class="image-overlay">
                <el-button type="primary" class="view-detail-btn">
                  查看详情
                </el-button>
              </div>
            </div>
            <div class="product-info">
              <h3>{{ product.name }}</h3>
              <p class="description">{{ product.shortDescription }}</p>
              <div class="tech-tags">
                <span 
                  v-for="(feature, index) in (product.features || []).slice(0, 2)" 
                  :key="index" 
                  class="tech-tag"
                >
                  {{ feature }}
                </span>
              </div>
            </div>
          </div>
          <div class="hover-effect"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { Picture } from '@element-plus/icons-vue'
import { fetchEnabledCategories, fetchPublishedProducts, mergeById, mergeProducts } from '@/utils/cms'

export default {
  name: 'ProductList',
  components: {
    Picture
  },
  directives: {
    scrollanimation: {
      mounted: (el) => {
        const observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              el.classList.add('animate-in')
              observer.unobserve(el)
            }
          },
          {
            threshold: 0.1
          }
        )
        observer.observe(el)
      }
    }
  },
  setup() {
    const router = useRouter()
    const scrollY = ref(0)
    const currentCategory = ref(1)

    const fallbackCategories = [
      { id: 1, name: 'X射线光学' },
      { id: 4, name: '光电二极管' },
      { id: 5, name: '微型傅里叶变换光谱仪' },
      { id: 6, name: '微型一体化射线管' },
      { id: 7, name: 'UV位置传感器' }
    ]

    const fallbackProducts = [
      {
        id: 1,
        name: '聚焦多毛细管光学元件',
        shortDescription: '聚焦 X 射线可实现小焦点和高强度。',
        image: '/images/product1.png',
        categoryId: 1,
        features: ['高精度聚焦', '可调节焦距']
      },
      {
        id: 2,
        name: '准直多毛细光学元件',
        shortDescription: '从发散输入光束到准平行输出光束。',
        image: '/images/product2.png',
        categoryId: 1,
        features: ['高效准直', '低发散度']
      },
      {
        id: 3,
        name: 'X 射线导光板',
        shortDescription: '有效传输和准直发散的 X 射线。',
        image: '/images/product3.png',
        categoryId: 1,
        features: ['简单可靠', '高效传输']
      },
      {
        id: 4,
        name: '成像光学元件',
        shortDescription: '用于 X 射线光束整形的高精度定制镜组。',
        image: '/images/product4.png',
        categoryId: 1,
        features: ['高精度成像', '空间分辨']
      },
      {
        id: 5,
        name: 'AXUV',
        shortDescription: '高性能光子、电子或X射线测量 (0.0124nm-190nm)',
        image: '/images/AXUV.png',
        categoryId: 4,
        features: ['100%内量子效率', '宽能量范围']
      },
      {
        id: 6,
        name: 'SXUV',
        shortDescription: 'EUV优化光电二极管 (1-190nm)，适用于13.5nm光刻',
        image: '/images/SXUV.png',
        categoryId: 4,
        features: ['稳定响应', 'EUV优化']
      },
      {
        id: 7,
        name: 'UVG',
        shortDescription: '可靠的紫外探测器 (190-400nm)，用于UV-A/B/C监测',
        image: '/images/UVG.png',
        categoryId: 4,
        features: ['高灵敏度', '低暗电流']
      },
      {
        id: 8,
        name: 'NIRONE Sensor S',
        shortDescription: '小巧便携的近红外光谱传感器 (1350-2450nm)',
        image: '/images/SensorS.png',
        categoryId: 5,
        features: ['现场测量', '快速可靠']
      },
      {
        id: 9,
        name: 'SXF-5010-C [XRF Source]',
        shortDescription: '紧凑型设计，最小焦斑0.3mm，适用于手持、便携式仪器。',
        image: '/images/SXF5010C.png',
        categoryId: 6,
        features: ['紧凑设计', '小焦斑 (0.3mm)']
      },
      {
        id: 10,
        name: 'SL Series X-ray Source',
        shortDescription: '紧凑型设计，最小焦斑0.3mm，适用于手持、便携式仪器。',
        image: '/images/SL.png',
        categoryId: 6,
        features: ['紧凑设计', '小焦斑 (0.3mm)']
      },
      // 新增UV位置传感器产品
      {
        id: 11,
        name: 'S1-0073-1L10UV_CP2',
        shortDescription: '一维UV位置传感器，10×2mm²有效区域，高线性度。',
        image: '/images/S1-0073-1L10UV_CP2_1.png',
        categoryId: 7,
        features: ['一维检测', '高线性度', 'UV优化']
      },
      {
        id: 12,
        name: 'S1-0074-1L20UV_CP3',
        shortDescription: '一维UV位置传感器，20×3mm²有效区域，大检测面积。',
        image: '/images/S1-0074-1L20UV_CP3_2.png',
        categoryId: 7,
        features: ['一维检测', '大检测面积', 'UV优化']
      },
      {
        id: 13,
        name: 'S2-0184-2L4_SU71',
        shortDescription: '二维UV位置传感器，4×4mm²有效区域，SMD封装。',
        image: '/images/S2-0184-2L4_SU71_1.png',
        categoryId: 7,
        features: ['二维检测', 'SMD封装', '紧凑设计']
      },
      {
        id: 14,
        name: 'S2-0034-2L20UV_SU9',
        shortDescription: '二维UV位置传感器，20×20mm²有效区域，大检测面积。',
        image: '/images/S2-0034-2L20UV_SU9_1.png',
        categoryId: 7,
        features: ['二维检测', '大检测面积', '高精度']
      },
      {
        id: 15,
        name: 'S2-0030-2L2UV_MP1',
        shortDescription: '二维UV位置传感器，2×2mm²有效区域，快速响应。',
        image: '/images/S2-0030-2L2UV_MP1_1.png',
        categoryId: 7,
        features: ['二维检测', '快速响应', '小检测面积']
      },
      {
        id: 16,
        name: 'S2-0016_2L10UV_SU7',
        shortDescription: '二维UV位置传感器，10×10mm²有效区域，陶瓷基板。',
        image: '/images/S2-0016_2L10UV_SU7_1.png',
        categoryId: 7,
        features: ['二维检测', '陶瓷基板', '中等检测面积']
      },
      {
        id: 17,
        name: 'S2-0006-2L4UV_MP1',
        shortDescription: '二维UV位置传感器，4×4mm²有效区域，TO-8封装。',
        image: '/images/S2-0006-2L4UV_MP1_1.png',
        categoryId: 7,
        features: ['二维检测', 'TO-8封装', '金属外壳']
      },
      {
        id: 18,
        name: 'S1-0356-D_A',
        shortDescription: 'PSD带外壳UV探测器，10×2mm²有效区域，DSUB9连接器。',
        image: '/images/S1-0356-K_A.png',
        categoryId: 7,
        features: ['带外壳', 'DSUB9连接器', '一维检测']
      },
      {
        id: 19,
        name: 'S1-0356-K_A',
        shortDescription: 'PSD带外壳UV探测器，10×2mm²有效区域，专业封装。',
        image: '/images/S1-0356-K_A.png',
        categoryId: 7,
        features: ['带外壳', '专业封装', '一维检测']
      },
      {
        id: 20,
        name: 'S2-0385-D',
        shortDescription: 'PSD带外壳UV探测器，4×4mm²有效区域，PGA陶瓷封装。',
        image: '/images/S2-0385-K.png',
        categoryId: 7,
        features: ['带外壳', 'PGA封装', '二维检测']
      },
      {
        id: 21,
        name: 'S2-0385-K',
        shortDescription: 'PSD带外壳UV探测器，4×4mm²有效区域，专业封装。',
        image: '/images/S2-0385-K.png',
        categoryId: 7,
        features: ['带外壳', '专业封装', '二维检测']
      },
      {
        id: 22,
        name: 'PSD Holder MH01',
        shortDescription: 'PSD带外壳UV探测器，专业光学封装，高精度。',
        image: '/images/PSD Holder MH01 data sheet.png',
        categoryId: 7,
        features: ['专业封装', '高精度', '光学优化']
      },
      {
        id: 23,
        name: 'SE-0018 SEEPOS',
        shortDescription: 'SE-0018系列产品，专业光学解决方案，高性能。',
        image: '/images/SE-0018 SEEPOS.png',
        categoryId: 7,
        features: ['专业解决方案', '高性能', '光学系统']
      },
      {
        id: 24,
        name: 'SE-0011-D_D',
        shortDescription: '二位电路放大器，信号处理电路，厚膜技术。',
        image: '/images/SE-0011-D_D_1.png',
        categoryId: 7,
        features: ['信号处理', '厚膜技术', '高可靠性']
      }
    ]

    const categories = ref(fallbackCategories)
    const products = ref(fallbackProducts)

    const filteredProducts = computed(() => {
      if (currentCategory.value === null || currentCategory.value === 'all') { 
        return products.value
      }
      return products.value.filter(p => p.categoryId === currentCategory.value)
    })

    const selectCategory = (categoryId) => {
      currentCategory.value = categoryId
    }

    const navigateToDetail = (product) => {
      router.push({
        name: 'ProductDetail',
        params: { id: product.id }
      })
    }

    const handleScroll = () => {
      scrollY.value = window.scrollY
    }

    onMounted(async () => {
      window.addEventListener('scroll', handleScroll)
      const [apiCategories, apiProducts] = await Promise.all([
        fetchEnabledCategories(),
        fetchPublishedProducts()
      ])
      if (apiCategories.length) {
        categories.value = mergeById(fallbackCategories, apiCategories)
      }
      if (apiProducts.length) {
        products.value = mergeProducts(fallbackProducts, apiProducts)
      }
    })

    onUnmounted(() => {
      window.removeEventListener('scroll', handleScroll)
    })

    return {
      scrollY,
      categories,
      currentCategory,
      filteredProducts,
      selectCategory,
      navigateToDetail
    }
  }
}
</script>

<style scoped>
.products-container {
  width: 100%;
}

/* 顶部横幅 */
.hero-section {
  height: 400px;
  background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-content {
  text-align: center;
  color: white;
  z-index: 2;
  padding: 0 20px;
}

.hero-content h1 {
  font-size: 48px;
  font-weight: 600;
  margin-bottom: 20px;
  letter-spacing: 2px;
}

.hero-content p {
  font-size: 24px;
  opacity: 0.9;
  letter-spacing: 1px;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.2);
  z-index: 1;
}

/* 分类导航优化 */
.category-nav {
  background: white;
  border-bottom: 1px solid #e8e8e8;
  position: sticky;
  top: 60px;
  z-index: 100;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  position: relative;
}

.nav-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px;
  display: flex;
  gap: 60px;
  justify-content: center;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none; /* Firefox */
}

.nav-container::-webkit-scrollbar {
  display: none;
}

.nav-item {
  padding: 20px 0;
  cursor: pointer;
  position: relative;
  transition: all 0.3s ease;
}

.nav-text {
  font-size: 18px;
  color: #4a5568;
  transition: color 0.3s ease;
}

.nav-line {
  position: absolute;
  bottom: -1px;
  left: 0;
  width: 100%;
  height: 2px;
  background: #1890ff;
  transform: scaleX(0);
  transition: transform 0.3s ease;
}

.nav-item:hover .nav-text {
  color: #1890ff;
}

.nav-item.active .nav-text {
  color: #1890ff;
}

.nav-item.active .nav-line {
  transform: scaleX(1);
}

/* 产品展示区 */
.products-section {
  padding: 60px 0;
  background: #f8fafc;
}

.products-grid {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 40px;
}

.product-card {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  background: white;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transition: all 0.4s ease;
}

.card-content {
  position: relative;
  z-index: 1;
}

.product-image {
  height: 280px;
  position: relative;
  overflow: hidden;
}

.product-image .el-image {
  width: 100%;
  height: 100%;
}

.product-image :deep(img) {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #f8f9fa;
  transition: transform 0.4s ease;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.4s ease;
}

.view-detail-btn {
  transform: translateY(20px);
  transition: transform 0.4s ease;
}

.product-info {
  padding: 30px;
}

.product-info h3 {
  font-size: 20px;
  color: #2c3e50;
  margin-bottom: 15px;
  font-weight: 600;
}

.description {
  font-size: 14px;
  line-height: 1.6;
  color: #4a5568;
  margin-bottom: 20px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.tech-tags {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.tech-tag {
  padding: 4px 12px;
  background: #e6f7ff;
  color: #1890ff;
  border-radius: 4px;
  font-size: 12px;
}

.hover-effect {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(24, 144, 255, 0.1) 0%, rgba(9, 109, 217, 0.1) 100%);
  opacity: 0;
  transition: opacity 0.4s ease;
}

/* 卡片悬停效果 */
.product-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
}

.product-card:hover .product-image img {
  transform: scale(1.05);
}

.product-card:hover .image-overlay {
  opacity: 1;
}

.product-card:hover .view-detail-btn {
  transform: translateY(0);
}

.product-card:hover .hover-effect {
  opacity: 1;
}

/* 滚动动画 */
[v-scrollanimation] {
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.6s ease;
}

[v-scrollanimation].animate-in {
  opacity: 1;
  transform: translateY(0);
}

/* 响应式调整 */
@media (max-width: 768px) {
  .hero-section {
    height: 300px;
  }

  .hero-content h1 {
    font-size: 32px;
  }

  .hero-content p {
    font-size: 18px;
  }

  .nav-container {
    min-width: 100vw;
    padding: 0 0;
    gap: 20px;
    justify-content: flex-start;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }

  .nav-item {
    padding: 15px 0;
    white-space: nowrap;
  }

  .products-grid {
    padding: 0 20px;
    gap: 20px;
  }

  .product-image {
    height: 200px;
  }

  .product-info {
    padding: 20px;
  }

  .category-nav {
    overflow-x: auto;
  }

  /* 渐变遮罩提示 */
  .category-nav::after {
    content: '';
    position: absolute;
    right: 0;
    top: 0;
    width: 32px;
    height: 100%;
    pointer-events: none;
    background: linear-gradient(to left, #fff 60%, rgba(255,255,255,0));
    z-index: 10;
  }
}
</style> 